import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const CATS = { transport: "Transport", entrepot: "Entrepôt", salaires: "Salaires", douane: "Douane", marketing: "Marketing", materiel: "Matériel", autre: "Autre" };
import { curSym as sym } from "@/lib/currency";
const fmt = (v, c) => `${Number(v || 0).toLocaleString("fr-FR")} ${sym(c)}`;

// Tableau Finance : revenus / dépenses / marges par Hub (£ UK, € France) et par source
// Avoirs en attente de remboursement (régularisation après réception d'un dossier payé)
function RefundsPending() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/refunds").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (list.length === 0) return null;
  return (
    <div className="mb-4 border border-amber-300 bg-amber-50 p-3" data-testid="refunds-pending-card">
      <div className="font-heading font-bold text-sm text-amber-800">Remboursements à confirmer ({list.length})</div>
      <div className="mt-2 space-y-2">
        {list.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 bg-card border border-border p-2 text-sm" data-testid={`refund-row-${r.number}`}>
            <span><span className="font-mono font-semibold">{r.number}</span> · {r.reference} · <b>{r.amount} {r.currency}</b>
              <span className="text-muted-foreground"> · {r.label}</span></span>
            <Button size="sm" data-testid={`refund-confirm-${r.number}`} className="rounded-sm h-7 bg-emerald-600 text-white text-xs"
              onClick={async () => {
                try { await api.post(`/admin/invoices/${r.id}/refund-confirm`); toast.success("Remboursement confirmé — email envoyé au client"); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
              }}>Confirmer le remboursement</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export const AdminFinance = ({ hub = "all", src = "all" }) => {
  const [fin, setFin] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const emptyForm = () => ({ label: "", category: "transport", amount: "", reference: "",
    hub: hub === "fr" ? "fr" : "uk", date: new Date().toISOString().slice(0, 10) });
  const [f, setF] = useState(emptyForm());
  const load = () => {
    const q = `hub=${hub}&src=${src}&period=${period}&date_from=${range.from}&date_to=${range.to}`;
    api.get(`/admin/finance?${q}`).then(({ data }) => setFin(data)).catch(() => {});
    api.get(`/admin/expenses?hub=${hub}`).then(({ data }) => setExpenses(data)).catch(() => {});
  };
  useEffect(() => { load(); setF(emptyForm()); }, [hub, src, period, range]); // eslint-disable-line
  if (!fin) return <p className="text-sm text-muted-foreground">Chargement…</p>;

  const curs = [...new Set([...Object.keys(fin.revenue_billed || {}), ...Object.keys(fin.revenue_paid),
    ...Object.keys(fin.expenses), ...Object.keys(fin.pending), ...Object.keys(fin.refunded),
    ...Object.keys(fin.logistics_cost || {})])];
  if (curs.length === 0) curs.push(hub === "uk" ? "GBP" : "EUR");

  const addExpense = async () => {
    try {
      await api.post("/admin/expenses", { ...f, amount: Number(f.amount) });
      toast.success("Dépense enregistrée");
      setOpen(false); setF(emptyForm()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delExpense = async (e) => {
    try { await api.delete(`/admin/expenses/${e.id}`); toast.success("Dépense supprimée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="finance-panel">
      <RefundsPending />
      {/* Période : aujourd'hui / semaine / mois / personnalisée */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="finance-period-bar">
        <div className="flex border border-border">
          {[["all", "Tout"], ["today", "Aujourd’hui"], ["week", "7 jours"], ["month", "30 jours"], ["custom", "Personnalisée"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`fin-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-3 py-1.5 text-xs font-semibold ${period === v ? "bg-primary text-white" : "bg-card hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input data-testid="fin-date-from" type="date" className="rounded-sm h-8 w-36" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <span className="text-xs text-muted-foreground">→</span>
            <Input data-testid="fin-date-to" type="date" className="rounded-sm h-8 w-36" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
        )}
      </div>
      {curs.map((c) => (
        <div key={c} className="mb-4">
          {curs.length > 1 && <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Devise {c} ({sym(c)})</div>}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {[["Chiffre d’affaires (facturé)", (fin.revenue_billed || {})[c], "fin-billed", "hero"],
              ["Paiements reçus", fin.revenue_paid[c], "fin-revenue", ""],
              ["Paiements en attente", fin.pending[c], "fin-pending", ""],
              ["Remboursements", fin.refunded[c], "fin-refunded", ""],
              ["Coût logistique (interne)", (fin.logistics_cost || {})[c], "fin-logistics", ""],
              ["Dépenses", fin.expenses[c], "fin-expenses", ""],
              ["Marge brute", (fin.gross_margin || {})[c], "fin-gross", "m"],
              ["Marge estimée", (fin.estimated_margin || {})[c], "fin-estimated", "m"],
              ["Marge réelle", (fin.real_margin || {})[c], "fin-real", "m"],
            ].map(([label, val, tid, style]) => (
              <div key={tid} className={`border p-3 ${style === "m"
                ? ((val || 0) >= 0 ? "border-emerald-600 bg-emerald-50" : "border-red-600 bg-red-50")
                : style === "hero" ? "border-border bg-primary text-white" : "border-border bg-card"}`}>
                <div className={`font-heading font-extrabold text-xl ${style === "m" ? ((val || 0) >= 0 ? "text-emerald-700" : "text-red-700") : ""}`}
                  data-testid={`${tid}-${c}`}>{fmt(val, c)}</div>
                <div className={`text-[11px] ${style === "hero" ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-6 flex items-center justify-between">
        <h3 className="font-heading font-bold">Dépenses ({expenses.length})</h3>
        <Button size="sm" data-testid="expense-add-btn" className="rounded-sm bg-accent text-white gap-1" onClick={() => setOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> Ajouter une dépense
        </Button>
      </div>
      <div className="mt-2 border border-border bg-card overflow-x-auto" data-testid="expenses-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Libellé</th>
              <th className="px-3 py-2">Catégorie</th>
              <th className="px-3 py-2">Hub</th>
              <th className="px-3 py-2">Montant</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-border last:border-0">
                <td className="px-3 py-2 text-muted-foreground">{e.date}</td>
                <td className="px-3 py-2 font-semibold">{e.label}{e.reference ? <span className="ml-1.5 font-mono text-xs text-muted-foreground">({e.reference})</span> : null}</td>
                <td className="px-3 py-2"><Badge className="rounded-sm bg-secondary text-primary">{CATS[e.category] || e.category}</Badge></td>
                <td className="px-3 py-2">{e.hub === "uk" ? "HUB UK" : "HUB FRANCE"}</td>
                <td className="px-3 py-2 font-bold">{fmt(e.amount, e.currency)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" data-testid={`expense-del-${e.id}`} className="rounded-sm h-7 text-red-600" onClick={() => delExpense(e)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {expenses.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Aucune dépense enregistrée.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Nouvelle dépense</DialogTitle></DialogHeader>
          <div className="grid gap-3 grid-cols-2">
            <div className="col-span-2">
              <Label className="text-xs">Libellé</Label>
              <Input data-testid="expense-label" className="rounded-sm" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Catégorie</Label>
              <select data-testid="expense-category" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                {Object.entries(CATS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Hub</Label>
              <select data-testid="expense-hub" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.hub}
                disabled={hub === "uk" || hub === "fr"} onChange={(e) => setF({ ...f, hub: e.target.value })}>
                <option value="uk">HUB UK (£)</option>
                <option value="fr">HUB FRANCE (€)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Montant ({f.hub === "uk" ? "£" : "€"})</Label>
              <Input data-testid="expense-amount" type="number" min="0" className="rounded-sm" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input data-testid="expense-date" type="date" className="rounded-sm" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Référence éventuelle</Label>
              <Input data-testid="expense-reference" className="rounded-sm" placeholder="Ex. : C2G-10023, DEP-UK-12…" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="expense-save" className="rounded-sm bg-accent text-white" onClick={addExpense}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
